<template>
  <div class="modal">
    <button @click="closeModal()" class="modal__Overlay"></button>
    <success-prompt :updated="updated" :message="prompt.success"></success-prompt>

    <div class="modal__Inner">
      <section class="modal__Post">
        <header class="modal__Header">
          <h1 class="modal__Title">
            {{ $store.getters.getStringTranslation($route.name.replace(/\s/g, '').toLowerCase()) }}
            <span v-if="l10n[lang].title">: {{ l10n[lang].title }}</span>
          </h1>

          <div class="modal__Meta">
            <ul>
              <li class="modal__MetaItem--Button" v-if="$route.params.id && previewLinks.length > 0">
                <span class="button--Small button--AccentThree">
                  {{ $store.getters.getStringTranslation('preview') }}
                  <ul>
                    <li v-for="(website, index) in previewLinks" :key="`website-preview-${index}`">
                      <a
                        :href="`${website.url}/${previewLinkBase(website)}/${slugify(
                          l10n[lang].title
                        )}?lang=${lang}&id=${$route.params.id}&showLogin=true`"
                        target="_blank"
                        >{{ website.url.replace('https://', '').replace('http://', '') }}</a
                      >
                    </li>
                  </ul>
                </span>
              </li>
              <li class="modal__MetaItem--Button">
                <button type="submit" @click="update()" :disabled="disableUpdate" class="button--Small">
                  {{ prompt.save }}
                </button>
              </li>
              <li class="modal__MetaItem">
                <button @click="closeModal()" class="modal__MetaLink">
                  <icon name="times"></icon>
                </button>
              </li>
            </ul>
          </div>
        </header>

        <div class="modal__Scrollable" id="editorModal">
          <error-prompt class="error--Modal" :errors="errors"></error-prompt>
          <loader class="imageLoader" :loading="loading || updating" />

          <template v-if="loading === false">
            <div class="cardPreview">
              <div class="cardPreview__Columns">
                <div class="cardPreview__Column" v-if="item.status !== undefined">
                  <h2 class="cardPreview__Title">{{ $store.getters.getStringTranslation('status') }}</h2>
                  <p class="cardPreview__Status">
                    {{ $store.getters.getStringTranslation(item.status) }}
                  </p>
                </div>
                <div class="cardPreview__Column">
                  <h2 class="cardPreview__Title">{{ $store.getters.getStringTranslation('languages') }}</h2>
                  <ul class="cardPreview__Meta">
                    <li v-for="translation in langs" :key="translation.id">
                      <!-- Regular link for exising translation -->
                      <template v-if="l10n[translation.toLowerCase()] !== undefined">
                        <router-link class="cardPreview__MetaLink" :to="translationURL(translation)">
                          {{ translation }}
                          <icon name="check" v-if="l10n[translation.toLowerCase()].title.length > 0"></icon>
                        </router-link>

                        <button
                          v-if="item.l10n.length > 1"
                          class="cardPreview__MetaPopup"
                          @click="removeTranslation(translation)"
                        >
                          <icon name="times"></icon>{{ $store.getters.getStringTranslation('remove') }}
                        </button>
                      </template>

                      <!-- New translation -->
                      <button class="cardPreview__MetaLink--Add" @click="addTranslation(translation)" v-else>
                        {{ translation }}
                        <icon name="plus" weight="solid"></icon>
                      </button>
                    </li>
                  </ul>
                </div>
                <div class="cardPreview__Column">
                  <h2 class="cardPreview__Title">{{ $store.getters.getStringTranslation('controls') }}</h2>
                  <ul class="cardPreview__Meta">
                    <li v-if="$route.params.id !== undefined">
                      <button class="cardPreview__MetaButton" @click="disable()">
                        <icon v-if="item.status === 'published'" name="eye-slash"></icon>
                        <icon v-else name="eye"></icon>
                        {{ disableLabel }}
                      </button>
                    </li>
                    <li>
                      <button class="cardPreview__MetaButton" @click="feature()">
                        <icon v-if="item.is_featured === false" name="star"></icon>
                        <icon v-else name="star" weight="solid"></icon
                        >{{ $store.getters.getStringTranslation('feature') }}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <template v-if="getProperty('title') || getProperty('description')">
              <div class="cardPreview__Post" v-if="scraperData.type === 'article' || scraperData.type === 'media'">
                <div class="cardPreview__PostHeader">
                  <div class="cardPreview__PostImage" v-if="item.contents.thumbnail_url">
                    <div
                      class="cardPreview__PostImagePreview"
                      :style="{ backgroundImage: `url(${item.contents.thumbnail_url})` }"
                    ></div>
                  </div>
                  <div>
                    <p class="cardPreview__PostCategory" v-if="item.tags !== undefined && item.tags.length > 0">
                      <span
                        v-for="tag in item.tags.filter((tag) =>
                          tag.l10n.map((translation) => translation.lang).includes(lang)
                        )"
                        :key="tag.id"
                        >{{ getTranslation(tag.l10n, 'name') }}</span
                      >
                    </p>
                    <h2 class="cardPreview__PostTitle">{{ getProperty('title') }}</h2>
                    <p class="cardPreview__PostSource">
                      <template v-if="l10n[lang].source_name">{{ l10n[lang].source_name }} |</template>
                      <time>{{ moment(item.start_datetime).format('DD.MM.YYYY.') }}</time>
                    </p>
                  </div>
                </div>
                <div class="cardPreview__PostContent">
                  <p>{{ getProperty('description') }}</p>
                </div>
              </div>

              <div
                class="cardPreview__Post--Document"
                v-else-if="scraperData.type === 'document' || scraperData.type === 'event'"
                :style="{ backgroundImage: `url(${item.contents.thumbnail_url})` }"
              >
                <div class="cardPreview__PostHeader">
                  <div>
                    <p v-if="scraperData.type === 'document'" class="cardPreview__PostType">
                      {{ item.type }}
                    </p>
                    <p class="cardPreview__PostCategory" v-if="item.tag_ids.length > 0">
                      <template v-for="taxonomy in taxonomies">
                        <span v-for="tag in item.tags.filter((tag) => tag.type === taxonomy.name)" :key="tag.id">{{
                          getTranslation(tag.l10n, 'name')
                        }}</span>
                      </template>
                    </p>
                    <h2 class="cardPreview__PostTitle">{{ getProperty('title') }}</h2>
                    <p class="cardPreview__PostSource">
                      <template v-if="l10n[lang].source_name">{{ l10n[lang].source_name }} |</template>
                      <time>{{ moment(item.start_datetime).format('DD.MM.YYYY.') }}</time>
                    </p>
                  </div>
                </div>
                <div class="cardPreview__PostContent">
                  <p>{{ getProperty('description') }}</p>
                </div>
              </div>
            </template>

            <form class="formSection">
              <div class="formSection__Row--Gray">
                <div>
                  <label class="formLabel" for="editURL">URL</label>
                  <div class="formSection__Columns--Editor">
                    <!-- If type equals 'event', change v-model to source.name -->
                    <input
                      v-if="
                        scraperData.type === 'event' && l10n[lang].source !== undefined && l10n[lang].source !== null
                      "
                      required
                      v-model="l10n[lang].source.url"
                      type="url"
                      class="formInput--Scrape"
                      id="editURL"
                    />
                    <input
                      v-else
                      required
                      v-model="l10n[lang].external_url"
                      type="url"
                      class="formInput--Scrape"
                      id="editURL"
                    />
                    <button
                      type="button"
                      @click="scrape()"
                      :disabled="scrappable"
                      class="button--Scrape"
                      v-if="this.scraperData.type !== 'document'"
                    >
                      {{ $store.getters.getStringTranslation('scrape') }}
                      <template v-if="scraperData.type === 'event'">event</template>
                      <template v-else>{{ scraperData.type }}</template>
                    </button>
                    <file-upload
                      v-if="['document', 'media'].includes(this.scraperData.type)"
                      :required="true"
                      v-model="l10n[lang].external_url"
                      :types="this.scraperData.type === 'media' ? ['audio', 'video'] : ['pdf', 'doc', 'xls', 'ppt']"
                    ></file-upload>
                  </div>
                </div>

                <p v-if="scraperData.type === 'document'" class="note">
                  {{ $store.getters.getStringTranslation('ifDocOnline') }}
                </p>

                <!-- Check if pasted URL has assigned the item.type from it's extension -->
                <p
                  v-if="
                    scraperData.type === 'document' &&
                    item.type &&
                    ['doc', 'pdf', 'ppt', 'xls'].indexOf(item.type) === -1
                  "
                  class="note--Important"
                >
                  {{ $store.getters.getStringTranslation('notProperFile') }}
                </p>

                <!-- Check if pasted URL has assigned the item.type from it's extension -->
                <p
                  v-if="
                    scraperData.type === 'document' &&
                    item.type &&
                    ['doc', 'pdf', 'ppt', 'xls'].indexOf(item.type) === -1
                  "
                  class="note--Important"
                >
                  Seems this file is not a proper multimeda file. We support mp3, mp4, avi, webm, jpg, png and webp.
                </p>
              </div>

              <div class="formSection__Row">
                <p>
                  <label class="formLabel" for="editTitle">{{ $store.getters.getStringTranslation('title') }}</label>
                  <input required v-model="l10n[lang].title" type="text" class="formInput" id="editTitle" />
                </p>
                <p v-if="l10n[lang].location_info !== undefined">
                  <label required class="formLabel" for="editLocationInfo">{{
                    $store.getters.getStringTranslation('locationInfo')
                  }}</label>
                  <input v-model="l10n[lang].location_info" type="text" class="formInput" id="editLocationInfo" />
                </p>
                <p v-if="item.start_datetime !== undefined">
                  <label class="formLabel" for="editDate">{{ $store.getters.getStringTranslation('date') }}</label>
                  <datepicker
                    :format="formatDate"
                    v-model="item.start_datetime"
                    class="formInput--Date"
                    id="editDate"
                  ></datepicker>
                </p>
                <p>
                  <label class="formLabel" for="editSource">{{
                    $store.getters.getStringTranslation('sourceName')
                  }}</label>
                  <input required type="text" class="formInput" v-model="l10n[lang].source_name" id="editSource" />
                </p>
                <p>
                  <label class="formLabel" for="editSubtitle">{{
                    $store.getters.getStringTranslation('subtitle')
                  }}</label>
                  <input required type="text" class="formInput" v-model="l10n[lang].subtitle" id="editSubtitle" />
                </p>
                <p>
                  <label class="formLabel" for="editForeword">{{
                    $store.getters.getStringTranslation('foreword')
                  }}</label>
                  <textarea
                    required
                    id="editForeword"
                    class="formInput--Textarea"
                    v-model="l10n[lang].foreword"
                  ></textarea>
                </p>
                <p>
                  <label class="formLabel" for="editDescription">{{
                    $store.getters.getStringTranslation('description')
                  }}</label>
                  <textarea
                    required
                    id="editDescription"
                    class="formInput--Textarea"
                    v-model="l10n[lang].description"
                  ></textarea>
                </p>

                <p v-if="item.api_keys.includes('be4a2ad5-f9e5-4e1f-80b9-abd80ce0e353')">
                  <label class="formLabel">Post content</label>
                  <text-editor v-model="l10n[lang].contents[0].text" />
                </p>

                <p>
                  <image-upload
                    :required="true"
                    v-model="item.contents.thumbnail_urls"
                    @removeImage="removeThumbURL"
                  ></image-upload>
                  <label for="photoCredit" class="formLabel">{{
                    $store.getters.getStringTranslation('photoCredit')
                  }}</label>
                  <input
                    id="photoCredit"
                    v-model="l10n[lang].photo_credits"
                    type="text"
                    class="formInput"
                    :placeholder="$store.getters.getStringTranslation('enterPhotoCredit')"
                  />
                </p>

                <div class="formSection__Row--Select" v-for="taxonomy in taxonomies" :key="taxonomy.id">
                  <search-tag
                    v-if="loading === false"
                    class="formSearchWrapper--Tags"
                    :showLabel="false"
                    :types="[taxonomy.name]"
                    :lang="lang"
                    :placeholder="`Choose ${taxonomy.name}`"
                    :icon="taxonomy.icon"
                    :tags="item.tags.filter((tag) => tag.type === taxonomy.name)"
                    v-model="taxonomy.data"
                    @onchange="syncTags"
                  />
                </div>
              </div>

              <div class="formSection__Row">
                <p>
                  <search-author
                    :multiple="true"
                    :lang="lang"
                    :required="false"
                    :authors="item.authors"
                    v-model="item.authors_id"
                  />
                </p>

                <p v-if="item.contents.additionals.contributor">
                  <label class="formLabel" for="contributor">Contributor</label>
                  <input
                    required
                    type="text"
                    class="formInput"
                    v-model="item.contents.additionals.contributor"
                    id="contributor"
                    placeholder="tag1, tag2, tag3"
                  />
                </p>

                <p v-if="item.contents.additionals.contributor_email">
                  <label class="formLabel" for="contributor_email">Contributor email</label>
                  <input
                    required
                    type="text"
                    class="formInput"
                    v-model="item.contents.additionals.contributor_email"
                    id="contributor_email"
                    placeholder="tag1, tag2, tag3"
                  />
                </p>

                <p>
                  <label class="formLabel"> {{ $store.getters.getStringTranslation('langStatus') }}</label>
                  <select class="formSelect" v-model="l10n[lang].status">
                    <option v-for="status in languageStatuses" :value="status" :key="status.id">
                      {{ $store.getters.getStringTranslation(status) }}
                    </option>
                  </select>
                </p>

                <p>
                  <label class="formLabel">{{ $store.getters.getStringTranslation('postStatus') }}</label>
                  <select class="formSelect" v-model="item.status">
                    <option v-for="status in statuses" :value="status" :key="status.id">
                      {{ $store.getters.getStringTranslation(status.replace(/\s/g, '').toLowerCase()) }}
                    </option>
                  </select>
                </p>

                <p v-if="item.status === 'password_protected'">
                  <label class="formLabel">{{ $store.getters.getStringTranslation('postPassword') }} </label>
                  <input
                    required
                    type="text"
                    class="formInput"
                    v-model="item.password"
                    id="password"
                    placeholder="•••••••••••••••••"
                  />
                </p>
              </div>

              <div class="formSection__Row">
                <label class="formLabel">{{ $store.getters.getStringTranslation('publishTo') }}</label>

                <p v-for="(website, index) in websites" :key="website.id">
                  <input
                    required
                    class="formCheckbox__Checkbox"
                    type="checkbox"
                    name="`publish-to-checkbox`"
                    :id="`publish-website-${index}`"
                    :value="website.api_key"
                    v-model="item.api_keys"
                  />
                  <label class="formCheckbox__Label" :for="`publish-website-${index}`">{{
                    website.url.replace('https://www.', '').replace('http://www.', '')
                  }}</label>
                </p>
              </div>
            </form>
          </template>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import EditorDefaults from '@/components/mixins/EditorDefaults.js'
import Slugify from '@/components/mixins/Slugify.js'
import TextEditor from '@/components/editor/TextEditor.vue'

export default {
  name: 'EditItem',
  mixins: [EditorDefaults, Slugify],
  components: {
    TextEditor,
  },
}
</script>
